import React, { useMemo, useState } from 'react';
import { Box, Typography, Chip, Paper, TextField, FormControl, InputLabel, Select, MenuItem, Button } from '@mui/material';
import { PiTreeStructure, PiMagnifyingGlass, PiX } from 'react-icons/pi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/apiService';
import { useJobGrouping, Job, JobGroup } from '../hooks/useJobGrouping';
import JobCard from './JobCard';
import SimpleJobDisplay from './SimpleJobDisplay';

/**
 * Feature flags for customizing UnifiedJobQueue behavior
 */
export interface JobQueueFeatures {
  /** Show pause/resume actions (default: false) */
  showPauseResume?: boolean;
  /** Show cancel action (default: false) */
  showCancel?: boolean;
  /** Show rename action (default: true) */
  showRename?: boolean;
  /** Show delete action (default: true) */
  showDelete?: boolean;
  /** Show JSON editor toggle in full variant (default: false) */
  showJsonEditor?: boolean;
  /** Show PDF viewer in full variant (default: false) */
  showPdfViewer?: boolean;
  /** Show review form for multi-step workflows (default: false) */
  showReviewForm?: boolean;
  /** Show "Chat with Files" button (default: false) */
  showChatWithFiles?: boolean;
  /** Show progress stepper for running jobs (default: false) */
  showProgressStepper?: boolean;
  /** Show document thumbnail (default: true) */
  showThumbnail?: boolean;
  /** Max completed groups to show (undefined = no limit) */
  maxCompletedGroups?: number;
  /** Group jobs by status (active/failed/completed sections) */
  groupByStatus?: boolean;
  /** Show built-in filters UI */
  showFilters?: boolean;
}

export interface UnifiedJobQueueProps {
  /**
   * Pre-filtered jobs array. If provided, component uses these directly.
   * If omitted, component fetches jobs internally using practice/workflow filters.
   */
  jobs?: Job[];
  
  /** Practice filter (only used when jobs prop is omitted) */
  practice?: string;
  
  /** Workflow filter (only used when jobs prop is omitted) */
  workflow?: string;
  
  /** Step workflow IDs to include (only used when jobs prop is omitted) */
  stepWorkflows?: string[];
  
  /**
   * Display variant:
   * - 'compact': Uses JobCard with expandable monitor (dashboard style)
   * - 'full': Uses SimpleJobDisplay with all features (workflow page style)
   */
  variant?: 'compact' | 'full';
  
  /** Feature flags to customize behavior. Overrides variant defaults. */
  features?: JobQueueFeatures;
  
  /** Callback when pause action is triggered */
  onPauseJob?: (jobId: string) => void;
  
  /** Callback when resume action is triggered */
  onResumeJob?: (jobId: string) => void;
  
  /** Callback when cancel action is triggered */
  onCancelJob?: (jobId: string) => void;
  
  /** Callback when rename action is triggered */
  onRenameJob?: (jobId: string, newName: string) => void;
  
  /** Callback when delete action is triggered */
  onDeleteJob?: (jobId: string) => void;
  
  /** Custom polling interval in ms (default: 5000 for running, 30000 otherwise) */
  pollingInterval?: number;
  
  /** Empty state message */
  emptyMessage?: string;
}

// Default features by variant
const compactDefaults: JobQueueFeatures = {
  showPauseResume: false,
  showCancel: false,
  showRename: true,
  showDelete: true,
  showJsonEditor: false,
  showPdfViewer: false,
  showReviewForm: false,
  showChatWithFiles: false,
  showProgressStepper: false,
  showThumbnail: true,
  groupByStatus: true,
  maxCompletedGroups: 10,
  showFilters: false,
};

const fullDefaults: JobQueueFeatures = {
  showPauseResume: false,
  showCancel: false,
  showRename: true,
  showDelete: true,
  showJsonEditor: true,
  showPdfViewer: true,
  showReviewForm: true,
  showChatWithFiles: true,
  showProgressStepper: true,
  showThumbnail: true,
  groupByStatus: false,
  showFilters: true,
};

/**
 * Render a group of related jobs with visual connection
 */
function JobGroupDisplay({ 
  group, 
  variant,
  features,
  onPauseJob, 
  onResumeJob, 
  onCancelJob, 
  onRenameJob, 
  onDeleteJob 
}: { 
  group: JobGroup;
  variant: 'compact' | 'full';
  features: JobQueueFeatures;
  onPauseJob?: (jobId: string) => void;
  onResumeJob?: (jobId: string) => void;
  onCancelJob?: (jobId: string) => void;
  onRenameJob?: (jobId: string, newName: string) => void;
  onDeleteJob?: (jobId: string) => void;
}) {
  const isMultiStep = group.jobs.length > 1;
  
  return (
    <Box sx={{ mb: 2 }}>
      {/* Multi-step group header */}
      {isMultiStep && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          mb: 1,
          pl: 1
        }}>
          <PiTreeStructure size={18} style={{ color: '#1976d2' }} />
          <Typography variant="caption" color="primary.main" fontWeight={500}>
            Multi-Step Workflow: {group.baseNumber}
          </Typography>
          <Chip 
            label={`${group.jobs.length} steps`} 
            size="small" 
            variant="outlined"
            sx={{ 
              height: 20, 
              fontSize: '0.7rem',
              borderColor: 'primary.main',
              color: 'primary.main',
            }}
          />
        </Box>
      )}
      
      {/* Jobs in group */}
      <Box sx={{ 
        position: 'relative',
        ...(isMultiStep && {
          borderLeft: '3px solid',
          borderColor: 'primary.light',
          pl: 2,
          ml: 1,
        })
      }}>
        {group.jobs.map((job, index) => (
          <Box 
            key={job.jobId || job.executionArn}
            sx={{ 
              position: 'relative',
              ...(isMultiStep && {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: -19,
                  top: 24,
                  width: 16,
                  height: 2,
                  bgcolor: 'primary.light',
                }
              })
            }}
          >
            {/* Step indicator for multi-step workflows */}
            {isMultiStep && (
              <Chip
                label={`Step ${index + 1}`}
                size="small"
                color={job.status === 'SUCCEEDED' ? 'success' : job.status === 'RUNNING' ? 'warning' : undefined}
                sx={{ 
                  position: 'absolute',
                  left: -60,
                  top: 20,
                  height: 20,
                  fontSize: '0.65rem',
                  zIndex: 1,
                  // Ensure readable contrast for default state
                  ...(job.status !== 'SUCCEEDED' && job.status !== 'RUNNING' && {
                    bgcolor: 'action.selected',
                    color: 'text.primary',
                  })
                }}
              />
            )}
            
            {/* Job display based on variant */}
            {variant === 'compact' ? (
              <JobCard
                job={job as any}
                onPause={onPauseJob || (() => {})}
                onResume={onResumeJob || (() => {})}
                onCancel={onCancelJob || (() => {})}
                onRename={onRenameJob || (() => {})}
                onDelete={onDeleteJob || (() => {})}
                showThumbnail={features.showThumbnail}
              />
            ) : (
              <SimpleJobDisplay 
                job={job as any}
                onRename={onRenameJob || (() => {})}
                onDelete={onDeleteJob || (() => {})}
              />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Unified Job Queue Component
 * 
 * Combines dashboard JobQueue and workflow page WorkflowJobQueue into a single
 * configurable component with feature flags for different use cases.
 * 
 * @example
 * // Dashboard usage (compact, with status sections)
 * <UnifiedJobQueue
 *   jobs={filteredJobs}
 *   variant="compact"
 *   features={{ showPauseResume: true, showCancel: true, groupByStatus: true }}
 *   onPauseJob={handlePause}
 *   onResumeJob={handleResume}
 *   onCancelJob={handleCancel}
 *   onRenameJob={handleRename}
 *   onDeleteJob={handleDelete}
 * />
 * 
 * @example
 * // Workflow page usage (full features, self-fetching)
 * <UnifiedJobQueue
 *   practice="ip-transactions"
 *   workflow="ocean-bank"
 *   stepWorkflows={['ocean-bank-step-one', 'ocean-bank-step-two']}
 *   variant="full"
 * />
 */
export default function UnifiedJobQueue({
  jobs: jobsProp,
  practice,
  workflow,
  stepWorkflows = [],
  variant = 'compact',
  features: featuresProp,
  onPauseJob,
  onResumeJob,
  onCancelJob,
  onRenameJob,
  onDeleteJob,
  pollingInterval,
  emptyMessage,
}: UnifiedJobQueueProps) {
  // Merge variant defaults with provided features
  const features = useMemo(() => ({
    ...(variant === 'compact' ? compactDefaults : fullDefaults),
    ...featuresProp,
  }), [variant, featuresProp]);

  // Filter state (only used when showFilters is true)
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  const queryClient = useQueryClient();
  
  // Self-fetching mode: fetch jobs when jobsProp is not provided
  const isSelfFetching = jobsProp === undefined;
  
  const { data: fetchedJobs = [] } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => apiService.getAllJobs(),
    enabled: isSelfFetching,
    refetchInterval: (query) => {
      if (pollingInterval) return pollingInterval;
      const jobs = query.state.data as Job[] || [];
      const hasRunningJobs = jobs.some((job) => 
        ['RUNNING', 'PENDING', 'WAITING_FOR_INPUT'].includes(job.status)
      );
      return hasRunningJobs ? 5000 : 30000;
    },
    staleTime: 1000,
  });

  // Build list of workflow IDs to match (for self-fetching mode)
  const workflowsToMatch = useMemo(() => {
    if (!workflow) return new Set<string>();
    const ids = new Set<string>([workflow]);
    // also match legacy __v2 suffixed IDs from older jobs
    ids.add(`${workflow}__v2`);
    stepWorkflows.forEach(sw => ids.add(sw));
    return ids;
  }, [workflow, stepWorkflows]);

  // Filter jobs for this specific workflow (self-fetching mode)
  const filteredByWorkflow = useMemo(() => {
    if (!isSelfFetching) return [];
    
    return fetchedJobs.filter((job: Job) => {
      // Must match practice
      if (practice && job.practice !== practice) {
        return false;
      }
      
      // Match workflow if specified
      if (workflow) {
        // base workflow name for step-prefix matching
        const baseWorkflow = workflow.endsWith('__v2') ? workflow.slice(0, -4) : workflow;
        const workflowMatches = job.workflows?.some((w: string) => {
          if (workflowsToMatch.has(w)) return true;
          if (w.startsWith(baseWorkflow + '-step-')) return true;
          return false;
        });
        
        if (!workflowMatches) return false;
      }

      return true;
    }).sort((a: Job, b: Job) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [fetchedJobs, practice, workflow, workflowsToMatch, isSelfFetching]);

  // Apply local filters (search, status, date) when showFilters is true
  const locallyFilteredJobs = useMemo(() => {
    const baseJobs = isSelfFetching ? filteredByWorkflow : (jobsProp || []);
    
    if (!features.showFilters) return baseJobs;

    return baseJobs.filter((job: Job) => {
      // Search filter
      const matchesSearch = !searchTerm || 
        (job.displayName || job.number || job.input?.number || '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      
      // Status filter
      const matchesStatus = !statusFilter || job.status === statusFilter;
      
      // Date filter
      let matchesDate = true;
      if (dateFilter) {
        const jobDate = new Date(job.createdAt);
        const today = new Date();
        switch (dateFilter) {
          case 'today':
            matchesDate = jobDate.toDateString() === today.toDateString();
            break;
          case 'week':
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesDate = jobDate >= weekAgo;
            break;
          case 'month':
            const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            matchesDate = jobDate >= monthAgo;
            break;
        }
      }
      
      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [isSelfFetching, filteredByWorkflow, jobsProp, features.showFilters, searchTerm, statusFilter, dateFilter]);

  // Final jobs list
  const jobs = locallyFilteredJobs;

  // Job mutations (for self-fetching mode)
  const renameJobMutation = useMutation({
    mutationFn: ({ jobId, newName }: { jobId: string; newName: string }) => 
      apiService.renameJob(jobId, newName),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] })
  });

  const deleteJobMutation = useMutation({
    mutationFn: (jobId: string) => apiService.deleteJob(jobId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] })
  });

  // Handle actions - use props if provided, otherwise use mutations
  const handleRename = onRenameJob || ((jobId: string, newName: string) => {
    renameJobMutation.mutate({ jobId, newName });
  });

  const handleDelete = onDeleteJob || ((jobId: string) => {
    deleteJobMutation.mutate(jobId);
  });

  // Use job grouping hook
  const groupingResult = useJobGrouping(jobs, {
    groupByStatus: features.groupByStatus,
    maxCompletedGroups: features.maxCompletedGroups,
  });

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setDateFilter('');
  };
  
  const hasActiveFilters = searchTerm || statusFilter || dateFilter;

  // Determine empty message
  const defaultEmptyMessage = hasActiveFilters 
    ? 'No jobs match your filters' 
    : (isSelfFetching ? 'No jobs submitted yet for this workflow' : 'No jobs submitted yet');

  return (
    <Box sx={{ pb: 2 }}>
      {/* Built-in Filters (when showFilters is true) */}
      {features.showFilters && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <PiMagnifyingGlass size={18} style={{ marginRight: 8, color: 'rgba(0, 0, 0, 0.54)' }} />
              }}
              sx={{ minWidth: 200 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="RUNNING">Running</MenuItem>
                <MenuItem value="SUCCEEDED">Completed</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
                <MenuItem value="QUEUED">Queued</MenuItem>
                <MenuItem value="WAITING_FOR_INPUT">Waiting</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Date</InputLabel>
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                label="Date"
              >
                <MenuItem value="">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">Past Week</MenuItem>
                <MenuItem value="month">Past Month</MenuItem>
              </Select>
            </FormControl>
            
            {hasActiveFilters && (
              <Button
                size="small"
                startIcon={<PiX size={18} />}
                onClick={clearFilters}
                variant="outlined"
              >
                Clear
              </Button>
            )}
          </Box>
          
          {hasActiveFilters && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {searchTerm && <Chip label={`Search: ${searchTerm}`} size="small" onDelete={() => setSearchTerm('')} />}
              {statusFilter && <Chip label={`Status: ${statusFilter}`} size="small" onDelete={() => setStatusFilter('')} />}
              {dateFilter && <Chip label={`Date: ${dateFilter}`} size="small" onDelete={() => setDateFilter('')} />}
            </Box>
          )}
        </Paper>
      )}

      {/* Status-grouped display (compact variant default) */}
      {features.groupByStatus ? (
        <>
          {/* Active Jobs */}
          {groupingResult.counts.active > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                🔄 Active ({groupingResult.counts.active})
              </Typography>
              {groupingResult.activeGroups.map(group => (
                <JobGroupDisplay
                  key={group.baseNumber}
                  group={group}
                  variant={variant}
                  features={features}
                  onPauseJob={onPauseJob}
                  onResumeJob={onResumeJob}
                  onCancelJob={onCancelJob}
                  onRenameJob={handleRename}
                  onDeleteJob={handleDelete}
                />
              ))}
            </Box>
          )}
          
          {/* Failed Jobs */}
          {groupingResult.counts.failed > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                ❌ Failed ({groupingResult.counts.failed})
              </Typography>
              {groupingResult.failedGroups.map(group => (
                <JobGroupDisplay
                  key={group.baseNumber}
                  group={group}
                  variant={variant}
                  features={features}
                  onPauseJob={onPauseJob}
                  onResumeJob={onResumeJob}
                  onCancelJob={onCancelJob}
                  onRenameJob={handleRename}
                  onDeleteJob={handleDelete}
                />
              ))}
            </Box>
          )}
          
          {/* Completed Jobs */}
          {groupingResult.counts.completed > 0 && (
            <Box>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                ✅ Completed ({groupingResult.counts.completed})
              </Typography>
              {groupingResult.completedGroups.map(group => (
                <JobGroupDisplay
                  key={group.baseNumber}
                  group={group}
                  variant={variant}
                  features={features}
                  onPauseJob={onPauseJob}
                  onResumeJob={onResumeJob}
                  onCancelJob={onCancelJob}
                  onRenameJob={handleRename}
                  onDeleteJob={handleDelete}
                />
              ))}
              {features.maxCompletedGroups && groupingResult.totalCompletedGroups > features.maxCompletedGroups && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                  Showing {features.maxCompletedGroups} of {groupingResult.totalCompletedGroups} completed workflow runs
                </Typography>
              )}
            </Box>
          )}
        </>
      ) : (
        /* Flat list display (full variant default) */
        <>
          {jobs.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Showing {jobs.length} job{jobs.length !== 1 ? 's' : ''} in {groupingResult.allGroups.length} workflow run{groupingResult.allGroups.length !== 1 ? 's' : ''}
              </Typography>
              {groupingResult.allGroups.map((group) => (
                <Box key={group.baseNumber} sx={{ mb: 3 }}>
                  <JobGroupDisplay
                    group={group}
                    variant={variant}
                    features={features}
                    onPauseJob={onPauseJob}
                    onResumeJob={onResumeJob}
                    onCancelJob={onCancelJob}
                    onRenameJob={handleRename}
                    onDeleteJob={handleDelete}
                  />
                </Box>
              ))}
            </>
          )}
        </>
      )}

      {/* Empty state */}
      {jobs.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {emptyMessage || defaultEmptyMessage}
        </Typography>
      )}
    </Box>
  );
}

// Named exports for convenience
export { type Job, type JobGroup } from '../hooks/useJobGrouping';
